import { Document, Image, Page, Text, View, pdf } from "@react-pdf/renderer";
import loadDistributionDiagramImage from "app/assets/load-distribution-diagram.png";
import i18next from "i18next";
import React from "react";
import { TFunction } from "react-i18next";
import {
  LOAD_DISTRIBUTION_PDF_FILE_NAME,
  LOAD_DISTRIBUTION_PDF_SECTIONS,
  LOAD_DISTRIBUTION_PDF_TITLE,
} from "../../data";
import { LoadDistributionPdfData } from "../../types";
import { PdfFieldRow, PdfInfoCard } from "./InfoCard";
import { PdfWeighingScreen } from "./WeighingScreen";
import { PdfWeighingNotes } from "./Sections";
import { PdfCalculationTable, PdfDualTablesSection } from "./Tables";
import { pdfStyles } from "./styles";

type LoadDistributionPdfDocumentProps = {
  t: TFunction;
  language: string;
  pdfData: LoadDistributionPdfData;
};

const LoadDistributionPdfDocument = ({
  t,
  language,
  pdfData,
}: LoadDistributionPdfDocumentProps) => {
  const { projectInfo, customerVehicle, technicalSpec } = pdfData;
  const midpoint = Math.ceil(technicalSpec.length / 2);
  const technicalSpecRows = technicalSpec.slice(midpoint).map((left, index) => ({
    left,
    right: technicalSpec.slice(0, midpoint)[index],
  }));

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <Text style={pdfStyles.mainTitle}>{t(LOAD_DISTRIBUTION_PDF_TITLE)}</Text>

        <View style={pdfStyles.topRow}>
          <PdfInfoCard
            titleKey={LOAD_DISTRIBUTION_PDF_SECTIONS.CUSTOMER_VEHICLE}
            fields={customerVehicle}
            t={t}
          />
          <PdfInfoCard
            titleKey={LOAD_DISTRIBUTION_PDF_SECTIONS.PROJECT_INFO}
            fields={projectInfo}
            t={t}
          />
        </View>

        <View>
          <Text style={pdfStyles.cardTitle}>
            {t(LOAD_DISTRIBUTION_PDF_SECTIONS.TECHNICAL_SPEC)}
          </Text>
          <View style={pdfStyles.topInfoCardBody}>
            {technicalSpecRows.map((row) => (
              <View
                key={`${row.left.labelKey}-${row.right.labelKey}`}
                style={pdfStyles.technicalSpecRow}
              >
                {[row.left, row.right].map((field) => (
                  <View key={field.labelKey} style={pdfStyles.technicalSpecField}>
                    <PdfFieldRow
                      labelKey={field.labelKey}
                      value={field.value}
                      t={t}
                    />
                  </View>
                ))}
              </View>
            ))}
          </View>
        </View>

        <View style={pdfStyles.diagramSection}>
          <Image src={loadDistributionDiagramImage} style={pdfStyles.diagramImage} />
        </View>

        <PdfCalculationTable t={t} pdfData={pdfData} />
        <PdfWeighingNotes t={t} pdfData={pdfData} />
        <PdfDualTablesSection t={t} language={language} pdfData={pdfData} />
        <View break>
          <PdfWeighingScreen t={t} pdfData={pdfData} />
        </View>
        <View break>
          <PdfWeighingScreen t={t} pdfData={pdfData} />
        </View>
      </Page>
    </Document>
  );
};

export const downloadLoadDistributionPdf = async (pdfData: LoadDistributionPdfData) => {
  const t = i18next.t.bind(i18next);
  const language = i18next.language || "hw";
  const blob = await pdf(
    <LoadDistributionPdfDocument t={t} language={language} pdfData={pdfData} />
  ).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${t(LOAD_DISTRIBUTION_PDF_FILE_NAME)}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default LoadDistributionPdfDocument;
